import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Appraiser,
  AppraiserContactDetails,
  AppraiserSpinnerResponse,
  AppraiserSearchParams,
} from '../models/appraiser.model';
import { GatewayService } from './gateway.service';

/** SMS email gateway used to display an appraiser's text address (legacy default: vtext.com). */
const SMS_GATEWAY_DOMAIN = 'vtext.com';

@Injectable({ providedIn: 'root' })
export class AppraiserService {
  private readonly http = inject(HttpClient);
  private readonly gateway = inject(GatewayService);
  private readonly baseUrl = environment.DISPATCH_BASE_URL;
  private readonly esbUrl = environment.ESB_URL;

  getAppraisers(params: AppraiserSearchParams): Observable<Appraiser[]> {
    const query: Record<string, string> = {
      filter: this.buildAppraiserFilter(params),
      orderBy:
        'userGroupOrd, userDistance, intervalCountToday, intervalCountYearMonthDay, intervalCountYearWeek, fullName',
      top: '20',
      format: 'json',
      refdate: `'${params.refdate}'`,
    };

    if (params.loclat && params.loclng) {
      query['loclat'] = params.loclat;
      query['loclng'] = params.loclng;
    }

    const payload = {
      target: '/dispatch/spinner',
      query,
    };

    return this.gateway
      .dispatchPost<AppraiserSpinnerResponse>(payload)
      .pipe(map((response) => response.results ?? []));
  }

  /** Escape single quotes for OData string literals. */
  private oDataEscape(value: string): string {
    return value.replace(/'/g, "''");
  }

  /** Strip trailing " County" so it matches region data (e.g. "Mercer County" → "Mercer"). */
  private normalizeCounty(county: string): string {
    return county.replace(/\s+county$/i, '').trim();
  }

  private buildAppraiserFilter(params: AppraiserSearchParams): string {
    // Name search (Search All): legacy sends only fullName + companyCd, nothing else.
    if (params.fullName) {
      const nameFilter = `fullName eq '${this.oDataEscape(params.fullName)}'`;
      return params.companyCd
        ? `${nameFilter} and companyCd eq '${this.oDataEscape(params.companyCd)}'`
        : nameFilter;
    }

    const unitInfo = params.unitInfo ?? '';
    const unitInfoLower = unitInfo.toLowerCase();
    const isVehicle = unitInfoLower.includes('veh-') || unitInfoLower.includes('rv-');

    // Non-vehicle (property): force LOB to 'HO', skip altCoverageCd
    const lobOverride = !isVehicle && params.lobCd !== 'HO';
    const lobCd = lobOverride ? 'HO' : params.lobCd;
    const parts: string[] = [`regionCompanyCd eq '${this.oDataEscape(params.companyCd)}'`];
    if (params.polStCd) parts.push(`polStCd eq '${this.oDataEscape(params.polStCd)}'`);
    if (params.routingChannelCd)
      parts.push(`routingChannelCd eq '${this.oDataEscape(params.routingChannelCd)}'`);
    parts.push(`regionLobCd eq '${lobCd}'`);
    if (params.severityQualification)
      parts.push(`severityQualification eq '${this.oDataEscape(params.severityQualification)}'`);
    if (params.coverageCd && !lobOverride)
      parts.push(`altCoverageCd eq '${this.oDataEscape(params.coverageCd)}'`);
    parts.push(`(regionStateCd eq '${this.oDataEscape(params.stateCd)}' or regionStateCd eq 'LL')`);
    if (params.unitType) {
      parts.push(`(unitType cn '${this.oDataEscape(params.unitType)}' or unitType cn 'UNK')`);
    } else {
      parts.push(`unitType cn 'UNK'`);
    }
    if (params.countyCd) {
      const county = this.normalizeCounty(params.countyCd);
      parts.push(`(regionCounty eq '${this.oDataEscape(county)}' or regionCounty eq 'ALL')`);
    }
    const zip5 = params.zipCd.substring(0, 5);
    parts.push(`(regionZipCd eq '${zip5}' or regionZipCd eq 'ALL')`);
    parts.push(`deleteInd eq 'N'`);
    parts.push(`assignType eq '${params.assignType ?? 'DISPATCH'}'`);
    parts.push(`companyCd eq '${this.oDataEscape(params.companyCd)}'`);

    return parts.join(' and ');
  }

  // Mirrors legacy GetFindAdjustersDefaultReferenceDate (dispatch_home.js):
  // legacy reads appointmentDate via a codes-lookup path that always returns
  // empty for date fields, so its apptDate branch is effectively dead code and
  // the spinner default is always tomorrow + skip-weekends. We do the same.
  calculateReferenceDate(): string {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const day = tomorrow.getDay();
    if (day === 0) tomorrow.setDate(tomorrow.getDate() + 1); // Sunday → Monday
    if (day === 6) tomorrow.setDate(tomorrow.getDate() + 2); // Saturday → Monday

    return this.formatDate(tomorrow);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private emptyContacts(streamRegionCd: string): AppraiserContactDetails {
    return {
      emailAddress: '',
      phoneNumber: '',
      businessPhone: null,
      businessExtension: null,
      cellPhone: null,
      textAddress: '',
      addressCounty: null,
      address: streamRegionCd.toLowerCase() === 'ne' ? 'MA' : 'NJ',
    };
  }

  /**
   * Address-only lookup for the assignment overview. Only vendors have a real address in the
   * response; for everyone else the address is the region default, so skip the userdetail call.
   */
  getAppraiserAddress(
    streamUserId: string,
    partyTypeCd: string,
    streamRegionCd: string,
  ): Observable<AppraiserContactDetails> {
    return partyTypeCd.toLowerCase() === 'vendor'
      ? this.getAppraiserContacts(streamUserId, partyTypeCd, streamRegionCd)
      : of(this.emptyContacts(streamRegionCd));
  }

  getAppraiserContacts(
    streamUserId: string,
    partyTypeCd: string,
    streamRegionCd: string,
  ): Observable<AppraiserContactDetails> {
    const empty = this.emptyContacts(streamRegionCd);

    if (!streamUserId || !streamRegionCd) return of(empty);

    const apiBase = this.esbUrl.replace(/dispatch\/$/, '');
    const isVendor = partyTypeCd.toLowerCase() === 'vendor';
    const url = isVendor
      ? `${apiBase}customer/details/${streamRegionCd}`
      : `${apiBase}common/userdetail/${streamRegionCd}`;

    const body = isVendor
      ? `<long>${streamUserId}</long>`
      : `<clientUserDetailRequestCriteria>
          <userId>${streamUserId}</userId>
          <returnUserCommunication>true</returnUserCommunication>
          <returnUserCredentialInfo>true</returnUserCredentialInfo>
          <returnUserAuthorizationInfo>false</returnUserAuthorizationInfo>
          <returnUserSecurityGroups>false</returnUserSecurityGroups>
          <returnUserWorkGroupInfo>true</returnUserWorkGroupInfo>
        </clientUserDetailRequestCriteria>`;

    return this.http
      .post(url, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/xml' }),
        responseType: 'text',
      })
      .pipe(
        map((xml) =>
          this.parseContactDetails(
            xml,
            isVendor,
            partyTypeCd.toLowerCase() === 'associate',
            empty.address,
          ),
        ),
        catchError(() => of(empty)),
      );
  }

  private buildTextAddress(phoneNumber: string): string {
    return phoneNumber ? `${phoneNumber}@${SMS_GATEWAY_DOMAIN}` : '';
  }

  private parseContactDetails(
    xmlText: string,
    isVendor: boolean,
    isAssociate: boolean,
    defaultAddress: string,
  ): AppraiserContactDetails {
    try {
      const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
      if (isVendor) return this.parseVendorContacts(doc);
      if (isAssociate) return this.parseAssociateContacts(doc, defaultAddress);
      return this.parseDefaultContacts(doc, defaultAddress);
    } catch {
      return {
        emailAddress: '',
        phoneNumber: '',
        businessPhone: null,
        businessExtension: null,
        cellPhone: null,
        textAddress: '',
        addressCounty: null,
        address: defaultAddress,
      };
    }
  }

  private parseVendorContacts(doc: Document): AppraiserContactDetails {
    // Mirrors legacy getAdjusterDetails: per emailChannels, prefer busn_email, then prsn_email,
    // then any address; the first emailChannels that yields an address wins.
    let emailAddress = '';
    for (const channels of doc.querySelectorAll('emailChannels')) {
      const dtos = Array.from(channels.querySelectorAll('addressChannelDTO'));
      const addressForUsage = (usage: string): string | undefined =>
        dtos
          .find((dto) => dto.querySelector('usageTypeCode')?.textContent?.includes(usage))
          ?.querySelector('interactionChannelTypeAddress')?.textContent ?? undefined;
      const found =
        addressForUsage('busn_email') ??
        addressForUsage('prsn_email') ??
        channels.querySelector('interactionChannelTypeAddress')?.textContent ??
        undefined;
      if (found !== undefined) {
        emailAddress = found;
        break;
      }
    }

    let businessPhone = '';
    for (const node of doc.querySelectorAll('phoneChannelDTO')) {
      if (node.querySelector('usageTypeCode')?.textContent?.includes('busn_phn')) {
        businessPhone = node.querySelector('phoneNumber')?.textContent ?? '';
        break;
      }
    }

    const now = new Date().toISOString().slice(0, 22);
    let matchRecordId = '';
    for (const node of doc.querySelectorAll('addressUsageDTO')) {
      const agreement = node.querySelector('agreementTypeCd')?.textContent ?? '';
      const usageType = node.querySelector('addressUsageTypeCd')?.textContent ?? '';
      const eff = (node.querySelector('effectiveDateTime')?.textContent ?? '').slice(0, 22);
      const exp = (node.querySelector('endDateTime')?.textContent ?? '').slice(0, 22);
      if (
        agreement.includes('clm') &&
        usageType.includes('phy_addr') &&
        eff <= now &&
        (!exp || exp >= now)
      ) {
        matchRecordId = node.querySelector('recordId')?.textContent ?? '';
        break;
      }
    }

    const addrDTO = matchRecordId
      ? Array.from(doc.querySelectorAll('addressDTO')).find(
          (node) => node.querySelector('recordId')?.textContent === matchRecordId,
        )
      : doc.querySelector('addressDTO');

    const addressCounty = addrDTO?.querySelector('subdivisionName')?.textContent ?? '';
    const address = [
      addrDTO?.querySelector('addressLine1')?.textContent ?? '',
      addrDTO?.querySelector('addressLine2')?.textContent ?? '',
      addrDTO?.querySelector('cityName')?.textContent ?? '',
      (addrDTO?.querySelector('postalSvcRegionCode')?.textContent ?? '').slice(0, 2),
      (addrDTO?.querySelector('postalCode')?.textContent ?? '').slice(0, 5),
      addrDTO?.querySelector('countryCode')?.textContent ?? '',
    ]
      .filter((part) => part && part !== 'unk')
      .join(' ')
      .trim();

    return {
      emailAddress,
      phoneNumber: businessPhone,
      businessPhone,
      businessExtension: null,
      cellPhone: null,
      textAddress: '',
      addressCounty,
      address,
    };
  }

  private parseAssociateContacts(doc: Document, defaultAddress: string): AppraiserContactDetails {
    let emailAddress = '';
    let businessPhone = '';
    let businessExtension = '';
    let cellPhone = '';

    for (const node of doc.querySelectorAll('userCommInformationCompositeDTO')) {
      if (!emailAddress) emailAddress = node.querySelector('userEmailAddr')?.textContent ?? '';
      for (const phoneNode of node.querySelectorAll('userPhoneDTO')) {
        const type = phoneNode.querySelector('phoneType')?.textContent ?? '';
        if (!businessPhone && type === 'busn_phn') {
          businessPhone = phoneNode.querySelector('phoneNo')?.textContent ?? '';
          businessExtension = phoneNode.querySelector('extNo')?.textContent ?? '';
        }
        if (!cellPhone && type === 'cell_phn') {
          cellPhone = phoneNode.querySelector('phoneNo')?.textContent ?? '';
        }
      }
    }

    const phoneNumber = cellPhone || businessPhone;
    const textAddress = this.buildTextAddress(phoneNumber);

    return {
      emailAddress,
      phoneNumber,
      businessPhone,
      businessExtension,
      cellPhone: cellPhone || null,
      textAddress,
      addressCounty: null,
      address: defaultAddress,
    };
  }

  private parseDefaultContacts(doc: Document, defaultAddress: string): AppraiserContactDetails {
    let emailAddress = '';
    let phoneNumber = '';

    for (const node of doc.querySelectorAll('userCommInformationCompositeDTO')) {
      if (!emailAddress) emailAddress = node.querySelector('userEmailAddr')?.textContent ?? '';
      if (!phoneNumber) phoneNumber = node.querySelector('phoneNo')?.textContent ?? '';
    }

    const textAddress = this.buildTextAddress(phoneNumber);

    return {
      emailAddress,
      phoneNumber,
      businessPhone: null,
      businessExtension: null,
      cellPhone: null,
      textAddress,
      addressCounty: null,
      address: defaultAddress,
    };
  }
}
